import axios, { AxiosError, AxiosResponse } from 'axios';
import { useAuth } from './auth/store';
import { 
    ERR_SESSION_EXPIRED,
    ENDPOINT_AUTH,
    ENDPOINT_AUTH_REAUTHORIZE,
    ENDPOINT_REG,
    ENDPOINT_FORGOT_PASS,
    ENDPOINT_AUTOLOGIN,
    ENDPOINT_LANDING_ICON,
    ENDPOINT_INVITE_DETAILS,
    ENDPOINT_AUTH_DETAILS,
    ENDPOINT_SIGNOUT,
    ERR_SESSION_INVALID
} from '../constants';
import { isSessionSwapping } from './sessionSwap';

// Create axios instance with default config
const axiosInstance = axios.create({
    withCredentials: true,
    timeout: 30000, // 30 seconds timeout
});

// Track if we're already showing the session expired modal to prevent multiple triggers
let isSessionExpiredHandling = false;

// Response interceptor for handling errors globally
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        // Reset the flag on successful responses
        if (response.data?.Code === "200" ||
            response.data?.Code === 200) {
            isSessionExpiredHandling = false;
        }
        return response;
    },
    (error: AxiosError) => {
        // Handle network errors
        if (!error.response) {
            console.error('Network error:', error.message);
            return Promise.reject(error);
        }
        
        // For HTTP errors, let them pass through
        return Promise.reject(error);
    }
);

// Add a second interceptor specifically for session expiration
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        // Check for session expiration in successful responses (HTTP 200 with error code)
        if (response.data?.Code === ERR_SESSION_EXPIRED || 
            response.data?.Code === ERR_SESSION_INVALID
        ) {
            handleSessionExpired(response);
        }
        return response;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

// Function to handle session expiration
const handleSessionExpired = (response: AxiosResponse) => {
    // Get the current request URL
    const requestUrl = response.config.url || '';

    // The session is being deliberately replaced (company switch). Requests racing that
    // swap fail against the terminated session - that is not an expiry, so don't prompt.
    if (isSessionSwapping()) {
        return;
    }

    // List of endpoints that should NOT trigger session expiration handling
    const excludedEndpoints = [
        ENDPOINT_AUTH,              // Login
        ENDPOINT_AUTH_REAUTHORIZE,  // Re-authentication
        ENDPOINT_REG,               // Registration
        ENDPOINT_FORGOT_PASS,       // Forgot password
        ENDPOINT_AUTOLOGIN,         // Auto-login (handles its own session expiration)
        ENDPOINT_LANDING_ICON,      // Landing icon
        ENDPOINT_INVITE_DETAILS,    // Public invite details
        ENDPOINT_AUTH_DETAILS, // Auth details endpoint
        ENDPOINT_SIGNOUT,  // Logout endpoint
    ];
    
    // Check if the current endpoint should be excluded
    const shouldExclude = excludedEndpoints.some(endpoint => requestUrl.includes(endpoint));
    //console.log('Request URL:', requestUrl, 'Should Exclude:', shouldExclude, 'Session Expired Handling:', isSessionExpiredHandling);
    if (!shouldExclude && !isSessionExpiredHandling) {
        isSessionExpiredHandling = true;
        
        // Get the auth store and trigger session expiration
        const authStore = useAuth.getState();
        //console.log('##### Triggering re-authentication UI...', response.data);
        
        // Extract MFA settings if available in the response
        const mfaSettings = response.data?.Msg ? {
            avatar: response.data.Msg.avatar || '',
            display: response.data.Msg.dis || '',
            isMfaEnabled: response.data.Msg.is_mfa_enabled || false,
            isPinEnabled: response.data.Msg.is_pin_enabled || false,
            isBiometricsEnabled: response.data.Msg.is_biometrics_enabled || false,
            isPasskeyEnabled: response.data.Msg.is_passkey_enabled || false,
            hasPinSetup: response.data.Msg.has_pin_setup || false,
            hasPasskeySetup: response.data.Msg.has_passkey_setup || false
        } : null;
        
        // Set session expired state which will trigger re-authentication UI
        authStore.setSessionExpired(true, mfaSettings);
        console.log('Session expired. Please re-authenticate.');
    }
};

// Function to reset session expired handling (call this after successful re-authentication)
export const resetSessionExpiredHandling = () => {
    isSessionExpiredHandling = false;
};

export default axiosInstance;